import React from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useProtection } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const typeLabels = {
  whole_life: 'Whole Life Insurance',
  term_life: 'Term Life Insurance',
  mortgage: 'Mortgage Insurance',
};

const typeIcons = {
  whole_life: '🛡️',
  term_life: '🛡️',
  mortgage: '🏠',
};

const Protection = () => {
  const { data: policies, loading, error, refresh } = useProtection();
  const totalCoverage = policies?.reduce((sum, p) => sum + p.coverage, 0) || 0;

  if (loading) return <LoadingState message="Loading protection policies..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!policies) return null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Protection</Text>
      
      <View style={styles.summaryCard}>
        <Text style={styles.summaryLabel}>Total Coverage</Text>
        <Text style={styles.summaryAmount}>${totalCoverage.toLocaleString()}</Text>
      </View>
      
      <View style={styles.policiesList}>
        {policies.map((policy, idx) => (
          <View key={idx} style={styles.policyItem}>
            <View style={styles.policyIcon}>
              <Text style={styles.iconText}>{typeIcons[policy.type] || '🛡️'}</Text>
            </View>
            <View style={styles.policyInfo}>
              <Text style={styles.policyName}>{typeLabels[policy.type] || policy.type}</Text>
              <Text style={styles.policyDetails}>
                {policy.provider} <Text style={styles.policyNumber}>(Policy #{policy.policy_number})</Text>
              </Text>
              <Text style={styles.premiumInfo}>Premium: ${policy.premium.toLocaleString()}/mo</Text>
              <Text style={styles.statusInfo}>
                Status: <Text style={policy.status === 'active' ? styles.statusActive : styles.statusInactive}>
                  {policy.status}
                </Text>
              </Text>
            </View>
            <Text style={styles.coverageAmount}>${policy.coverage.toLocaleString()}</Text>
          </View>
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 768, // max-w-3xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  summaryLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  summaryAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#2563EB',
  },
  policiesList: {
    gap: 16,
  },
  policyItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  policyIcon: {
    marginRight: 16,
  },
  iconText: {
    fontSize: 32,
  },
  policyInfo: {
    flex: 1,
  },
  policyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  policyDetails: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 2,
  },
  policyNumber: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  premiumInfo: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  statusInfo: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  statusActive: {
    color: '#059669',
  },
  statusInactive: {
    color: '#DC2626',
  },
  coverageAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
});

export default Protection; 